import { moveSlot, insertSlot, removeSlot, reindex, remapSelection } from '../engine/roster/rack';
import deepClone from '../utils/deepClone';

/**
 * A plate detached from the weapon it came from.
 *
 * `weaponId` MUST NOT survive a clone: two plates carrying the same id are
 * two weapons sharing one magazine. `slotsToWeapons` mints a fresh id for
 * a repeat as a backstop, but stripping it here is what makes the copy a
 * new weapon rather than a second view of the old one.
 */
function detach(slot) {
  const copy = deepClone(slot);
  delete copy.weaponId;
  delete copy.sourceProfile;
  return copy;
}

/** The armed plate at `idx`, or null for none or an empty one. */
const plateAt = (slots, idx) => {
  const src = slots[idx];
  return src && !src.isEmpty ? src : null;
};

/** A real position on a rack of `length`. */
const onRack = (i, length) => i != null && i >= 0 && i < length;

/** Both ends real, distinct positions on a rack of `length`. */
const isRealMove = (from, to, length) => from !== to && onRack(from, length) && onRack(to, length);

/**
 * The rack after saving from the editor. An `id` past the end APPENDS -
 * that is what "＋ Arm a weapon" relies on, since it opens the editor at
 * `currentSlots.length` against an `emptySlot` draft.
 */
function savedRack(slots, armed) {
  if (armed.id >= slots.length) return reindex([...slots, armed]);
  const next = [...slots];
  next[armed.id] = armed;
  return reindex(next);
}

/**
 * Armoury rack operations: duplicate, cut, copy, paste, delete, move, save.
 *
 * The list algebra lives in `engine/roster/rack.ts` and is tested there;
 * this is the `setAtkProfiles` plumbing on top of it, plus the one thing
 * that cannot be pure - keeping the armed weapon pointed at the same
 * weapon after the rack changes shape.
 *
 * WHY SELECTION REMAPPING IS NOT OPTIONAL: `selectedSlot` is an index and
 * the ATTACK footer stays live whatever it points at. On the old 40-cell
 * grid a stale index landed on an empty cell and the footer hid itself. On
 * a rack every index is a real weapon, so an un-remapped index silently
 * arms the wrong gun.
 *
 * @param {object} params
 * @param {Array}    params.currentSlots        - the rack, dense
 * @param {string}   params.localCurrentProfile - active profile name
 * @param {number|null} params.selectedSlot     - the armed position
 * @param {Function} params.setSelectedSlot     - armed position setter
 * @param {object|null} params.slotClipboard    - cut/copied plate, if any
 * @param {Function} params.setSlotClipboard    - clipboard setter
 * @param {Function} params.setAtkProfiles      - attacker profiles setter
 * @param {Function} params.setEditingSlot      - editor setter (cleared on save)
 * @param {Function} params.isLocked            - permission lock checker
 */
export default function useSlotManagement(params) {
  const { localCurrentProfile, setAtkProfiles, selectedSlot, setSelectedSlot } = params;
  const ctx = {
    ...params,
    locked: () => params.isLocked('editProfiles'),
    /** Rewrite the active profile's rack through `fn`. */
    updateRack: (fn) =>
      setAtkProfiles((p) => {
        const next = { ...p, list: { ...p.list } };
        next.list[localCurrentProfile] = fn(next.list[localCurrentProfile] ?? []);
        return next;
      }),
    /** Keep the armed weapon armed after the rack changes shape. */
    remap: (change) => setSelectedSlot(remapSelection(selectedSlot, change)),
  };
  return { ...clipboardVerbs(ctx), ...rackVerbs(ctx) };
}

/** Duplicate, copy, cut and paste. */
function clipboardVerbs({ currentSlots, localCurrentProfile, slotClipboard, setSlotClipboard, ...ctx }) {
  const clip = (src) => setSlotClipboard({ ...detach(src), sourceProfile: localCurrentProfile });
  const handleDuplicateSlot = (idx) => {
    const src = ctx.locked() ? null : plateAt(currentSlots, idx);
    if (!src) return;
    // Directly below the original, where the user is already looking.
    ctx.updateRack((slots) => insertSlot(slots, idx + 1, detach(src)));
    ctx.remap({ kind: 'insert', to: idx + 1 });
  };
  const handleCopySlot = (idx) => {
    const src = plateAt(currentSlots, idx);
    if (!src) return;
    clip(src);
  };
  const handleCutSlot = (idx) => {
    const src = ctx.locked() ? null : plateAt(currentSlots, idx);
    if (!src) return;
    clip(src);
    ctx.updateRack((slots) => removeSlot(slots, idx));
    ctx.remap({ kind: 'remove', from: idx });
  };
  const handlePasteSlot = pasteVerb({ slotClipboard, setSlotClipboard, ...ctx });
  return { handleDuplicateSlot, handleCutSlot, handleCopySlot, handlePasteSlot };
}

/**
 * Paste INSERTS. There are no empty cells to overwrite, and the
 * clipboard's remaining job is carrying a weapon between characters -
 * within one rack, drag and the ▲▼ controls do it better.
 */
function pasteVerb({ slotClipboard, setSlotClipboard, locked, updateRack, remap }) {
  return (idx) => {
    if (locked() || !slotClipboard) return;
    const plate = detach(slotClipboard);
    setSlotClipboard(null);
    updateRack((slots) => insertSlot(slots, idx, plate));
    remap({ kind: 'insert', to: idx });
  };
}

/** Delete, move and save. */
function rackVerbs({ currentSlots, setEditingSlot, setSelectedSlot, locked, updateRack, remap }) {
  const handleDeleteSlot = (idx) => {
    if (locked()) return;
    updateRack((slots) => removeSlot(slots, idx));
    remap({ kind: 'remove', from: idx });
  };
  /** Drag-and-drop and keyboard reorder. Insert semantics, never swap. */
  const handleMoveSlot = (from, to) => {
    if (locked() || !isRealMove(from, to, currentSlots.length)) return;
    updateRack((slots) => moveSlot(slots, from, to));
    remap({ kind: 'move', from, to });
  };
  /** The ▲▼ controls and Ctrl+Arrow. A no-op at the ends. */
  const handleMoveSlotBy = (idx, delta) => handleMoveSlot(idx, idx + delta);
  const handleSaveSlot = (slotData) => {
    if (locked()) return;
    const armed = { ...slotData, isEmpty: false };
    updateRack((slots) => savedRack(slots, armed));
    setEditingSlot(null);
    setSelectedSlot(Math.min(armed.id, currentSlots.length));
  };
  return { handleDeleteSlot, handleMoveSlot, handleMoveSlotBy, handleSaveSlot };
}
